
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/SafeAuthProvider";
import { useSettings } from "@/context/SettingsContext";
import { useToast } from "@/components/ui/use-toast";
import { JournalEntry } from "@/hooks/journal/types";
import { getRpgThemeClasses } from "@/lib/utils";
import { cn } from "@/lib/utils";
import HistoryHeader from "@/components/history/HistoryHeader";
import HistoryEmptyState from "@/components/history/HistoryEmptyState";
import JournalEntriesList from "@/components/history/JournalEntriesList";

const History = () => {
  const { user } = useAuth();
  const { darkMode, enableRpgMode } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);
  
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  useEffect(() => {
    if (!user) {
      setJournalEntries([]);
      setLoading(false);
      return;
    }
    
    const fetchJournalEntries = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("journal_entries")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });
          
        if (error) {
          throw error;
        }
        
        // Ensure each entry has progress properties, default to 0/7
        const entriesWithProgress = data?.map(entry => ({
          ...entry,
          progress: entry.progress || 0,
          progress_max: entry.progress_max || 7
        })) || [];
        
        // Sort entries: incomplete entries first (by created_at desc), then complete entries (by created_at desc)
        const sortedEntries = entriesWithProgress.sort((a, b) => {
          const aComplete = a.progress >= (a.progress_max || 7);
          const bComplete = b.progress >= (b.progress_max || 7);
          
          // If completion status is different, sort incomplete first
          if (aComplete !== bComplete) {
            return aComplete ? 1 : -1;
          }
          
          // If both have same completion status, sort by created_at (newest first)
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        
        setJournalEntries(sortedEntries as JournalEntry[]);
      } catch (error) {
        console.error("Error fetching journal entries:", error);
        toast({
          title: "Error",
          description: "Failed to load journal entries.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchJournalEntries();
  }, [user, toast]);
  
  const handleViewEntry = (entryId: string) => {
    navigate(`/journal/${entryId}`);
  };
  
  const handleCreateNew = () => {
    navigate("/journal");
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!user) return;
    
    try {
      setIsDeleting(true);
      setEntryToDelete(entryId);
      
      const { error } = await supabase
        .from("journal_entries")
        .delete()
        .eq("id", entryId)
        .eq("user_id", user.id);
      
      if (error) throw error;
      
      // Update local state to remove the deleted entry
      setJournalEntries(journalEntries.filter(entry => entry.id !== entryId));
      
      toast({
        title: "Entry deleted",
        description: "Journal entry has been successfully deleted."
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error.message || "Failed to delete journal entry."
      });
      console.error("Error deleting entry:", error);
    } finally {
      setIsDeleting(false);
      setEntryToDelete(null);
    }
  };

  return (
    <div className={cn(
      "w-full max-w-4xl mx-auto py-4 sm:py-6 px-4",
      enableRpgMode && rpgTheme.background + " min-h-screen"
    )}>
      <HistoryHeader 
        onCreateNew={handleCreateNew} 
        journalEntries={journalEntries}
      />
      
      {journalEntries.length === 0 && !loading ? (
        <HistoryEmptyState onCreateNew={handleCreateNew} />
      ) : (
        <JournalEntriesList
          journalEntries={journalEntries}
          loading={loading}
          isDeleting={isDeleting}
          entryToDelete={entryToDelete}
          onViewEntry={handleViewEntry}
          onDeleteEntry={handleDeleteEntry}
        />
      )}
    </div>
  );
};

export default History;
